import React from 'react';
import { Dialog, DialogContent, Typography, makeStyles, Box, Button } from '@material-ui/core';
import ImageIcon from '@material-ui/icons/Image';
import { CommonButton } from './CommonComponents';
import { CouponMasterItemTableRow } from '../../../../types/couponPackMaster';

type ModalConfirmCouponPackSmartCheckProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentCouponSelected?: CouponMasterItemTableRow;
  loading?: boolean;
  useCount?: number;
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 8,
      padding: '20px 22px 17px',
      width: '100%',
      maxWidth: 323,
      boxSizing: 'border-box',
      margin: theme.spacing(2),
    },
    '& .MuiDialogContent-root': {
      padding: 0,
    },
  },
  couponPackTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#303030',
    marginBottom: theme.spacing(2),
    lineHeight: '160%',
    wordBreak: 'break-all',
  },
  couponWrapper: {
    display: 'flex',
    gap: '24px',
  },
  couponImageWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  couponImage: {
    width: '100%',
    maxWidth: '104px',
    height: 'auto',
  },
  quantityWrapper: {
    marginBottom: theme.spacing(0.5),
  },
  quantityLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#303030',
    marginRight: 4,
    lineHeight: '160%',
  },
  quantityValue: {
    fontSize: 22,
    fontWeight: 600,
    color: '#FF3B30',
  },
  quantityUnit: {
    fontSize: 14,
    fontWeight: 300,
    color: '#303030',
  },

  totalAmountWrapper: {
    marginBottom: theme.spacing(1.5),
  },
  totalAmountLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#303030',
  },
  totalAmountValue: {
    fontSize: 22,
    fontWeight: 600,
    color: '#FF3B30',
  },
  totalAmountUnit: {
    fontSize: 12,
    fontWeight: 300,
    color: '#303030',
  },
  codeWrapper: {
    marginBottom: theme.spacing(1.5),
  },
  codeLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#303030',
  },
  codeValue: {
    fontSize: 16,
    fontWeight: 600,
    color: '#303030',
  },
  confirmationTextWrapper: {
    padding: '8px 10px 11px 12px',
    backgroundColor: '#F9F9F9',
    marginBottom: '16px',
  },
  confirmationText: {
    fontSize: 16,
    fontWeight: 600,
    color: '#000000',
    textAlign: 'center',
    marginBottom: theme.spacing(0.5),
    lineHeight: '160%',
  },
  warningText: {
    fontSize: 12,
    fontWeight: 300,
    color: '#F94F4F',
    lineHeight: '160%',
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  imageDefault: {
    width: '104px',
    height: 'auto',
    color: '#d3d3d3',
  },
  storeName: {
    fontSize: 12,
    fontWeight: 600,
    color: '#000000',
    lineHeight: '160%',
    marginBottom: '16px',
  },
  storeNameSub: {
    fontSize: 16,
    fontWeight: 300,
    color: '#000000',
  },
  commonButton: {
    width: '100%',
    height: '56px',
    borderRadius: '999px',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
  },
}));

export const ModalConfirmCouponPackSmartCheck: React.FC<ModalConfirmCouponPackSmartCheckProps> = ({
  open,
  onClose,
  onConfirm,
  currentCouponSelected,
  loading = false,
  useCount = 1,
}) => {
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      aria-labelledby="modal-confirm-coupon-pack-smart-check"
    >
      <DialogContent>
        <Typography className={classes.couponPackTitle}>{currentCouponSelected?.couponMasterName}</Typography>

        <Box className={classes.couponWrapper}>
          <Box className={classes.couponImageWrapper}>
            {currentCouponSelected?.imageUrl ? (
              <img src={currentCouponSelected.imageUrl} alt="Product Image" className={classes.couponImage} />
            ) : (
              <ImageIcon className={classes.imageDefault} />
            )}
          </Box>

          <Box className={classes.quantityWrapper}>
            <Typography className={classes.quantityLabel}>使う枚数</Typography>
            <Typography className={classes.totalAmountValue}>
              {useCount}
              <span className={classes.totalAmountUnit}>枚</span>
            </Typography>
            {!!currentCouponSelected?.couponAmount && (
              <Box className={classes.totalAmountWrapper}>
                <Typography className={classes.totalAmountLabel}>金額</Typography>
                <Typography className={classes.totalAmountValue}>
                  {(currentCouponSelected?.couponAmount * useCount).toLocaleString('en-US')}
                  <span className={classes.totalAmountUnit}>円分</span>
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Typography className={classes.storeName}>
          使用店舗
          <br />
          <span className={classes.storeNameSub}></span>
        </Typography>

        <Box className={classes.confirmationTextWrapper}>
          <Typography className={classes.confirmationText}>
            上記内容で使用します。
            <br />
            本当によろしいでしょうか？
          </Typography>

          <Typography className={classes.warningText}>
            ※スタッフにこの画面を提示後、「使用する」ボタンを押してください。
          </Typography>
        </Box>

        <Box className={classes.buttonWrapper}>
          {currentCouponSelected?.smartCheckImageUrl &&
          currentCouponSelected?.smartCheckImageUrl.smartCheckConfirmButtonImageUrl ? (
            <Button onClick={onConfirm} disabled={loading} style={{ padding: 0, width: '100%' }}>
              <img
                src={currentCouponSelected.smartCheckImageUrl.smartCheckConfirmButtonImageUrl}
                alt="Smart Check Confirm Button Image"
                className={classes.commonButton}
              />
            </Button>
          ) : (
            <CommonButton onClick={onConfirm} label="使用する" variant="danger" disabled={loading} />
          )}

          {currentCouponSelected?.smartCheckImageUrl &&
          currentCouponSelected?.smartCheckImageUrl.smartCheckConfirmCancelButtonImageUrl ? (
            <Button onClick={onClose} disabled={loading} style={{ padding: 0, width: '100%' }}>
              <img
                src={currentCouponSelected.smartCheckImageUrl.smartCheckConfirmCancelButtonImageUrl}
                alt="Smart Check Confirm Cancel Button Image"
                className={classes.commonButton}
              />
            </Button>
          ) : (
            <CommonButton onClick={onClose} label="前に戻る" variant="secondary" />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
